import { useCallback, useEffect, useRef, useState } from 'react';
import { detectWickelineBanner } from './wickelineDetector';
import { createWickelineTracker } from './wickelineTracker';

const SAMPLE_INTERVAL_MS = 500; // 처치 배너는 2~3초만 떠 있어서 작은 영역만 0.5초마다 확인한다
const RING_SIZE = 24; // 처치 알림이 떴을 때의 프레임을 최근 24장까지 보관(0.5초 간격이라 알림 한 번에 6장 안팎)
const SHEET_FRAMES = 12; // 저장하는 한 장짜리 이미지에 넣는 프레임 수
const DIAG_INTERVAL_MS = 1500; // 점수 표시를 갱신하는 간격(화면을 자주 다시 그리지 않게)

const INITIAL_DIAG = { killAlerts: 0, wickelineKills: 0, lastScore: null, maxScore: 0, saved: 0 };

/**
 * 게임 중(enabled)에 왼쪽 알림 영역에서 처치 배너를 0.5초마다 확인한다.
 * - 위클라인 처치가 새로 일어나면 onKill(nowMs)을 한 번 부른다(배너가 계속 보여도 한 번만).
 * - 진단: 어떤 대상이든 처치 알림(아이콘)이 뜬 프레임을 최근 24장 보관해 한 장의 PNG로 저장할 수 있게 한다.
 *   실제 게임에서 위클라인 처치를 놓쳤을 때, 알림이 떴는지와 어떤 모양이었는지를 이 파일로 확인한다.
 * 반환: { diag: { killAlerts, wickelineKills, lastScore(위클라인 사진 점수), maxScore, saved }, saveFrames() }
 */
export default function useWickelineWatch({ enabled, captureWickelineImage, resetToken, onKill }) {
  const wickelineTrackerRef = useRef(null);
  const alertTrackerRef = useRef(null);
  if (!wickelineTrackerRef.current) {
    wickelineTrackerRef.current = createWickelineTracker();
    alertTrackerRef.current = createWickelineTracker();
  }
  const captureRef = useRef(captureWickelineImage);
  captureRef.current = captureWickelineImage;
  const onKillRef = useRef(onKill);
  onKillRef.current = onKill;
  const ringRef = useRef([]);
  const statsRef = useRef({ ...INITIAL_DIAG });
  const [diag, setDiag] = useState(INITIAL_DIAG);

  const publish = useCallback(() => {
    const s = statsRef.current;
    setDiag((prev) => (
      prev.killAlerts === s.killAlerts && prev.wickelineKills === s.wickelineKills && prev.lastScore === s.lastScore
      && prev.maxScore === s.maxScore && prev.saved === ringRef.current.length
        ? prev
        : { ...s, saved: ringRef.current.length }
    ));
  }, []);

  // 새 게임이 시작되면 진단 정보와 추적 상태를 비운다
  useEffect(() => {
    wickelineTrackerRef.current.reset();
    alertTrackerRef.current.reset();
    ringRef.current = [];
    statsRef.current = { ...INITIAL_DIAG };
    setDiag(INITIAL_DIAG);
  }, [resetToken]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const sample = () => {
      const image = captureRef.current && captureRef.current();
      if (!image) {
        return;
      }
      const now = Date.now();
      const result = detectWickelineBanner(image);
      const stats = statsRef.current;
      stats.lastScore = Math.round(result.portraitScore * 100) / 100;
      stats.maxScore = Math.max(stats.maxScore, stats.lastScore);
      if (result.killBanner || result.matched) {
        ringRef.current = [
          ...ringRef.current,
          { at: new Date(now), image, scores: { full: result.score, portrait: result.portraitScore, icon: result.iconScore }, matched: result.matched },
        ].slice(-RING_SIZE);
      }
      if (alertTrackerRef.current.update(result.killBanner || result.matched, now)) {
        stats.killAlerts += 1;
      }
      if (wickelineTrackerRef.current.update(result.matched, now)) {
        stats.wickelineKills += 1;
        publish();
        if (onKillRef.current) {
          onKillRef.current(now);
        }
      }
    };
    sample();
    const sampleTimer = setInterval(sample, SAMPLE_INTERVAL_MS);
    const diagTimer = setInterval(publish, DIAG_INTERVAL_MS);
    return () => {
      clearInterval(sampleTimer);
      clearInterval(diagTimer);
    };
  }, [enabled, publish]);

  // 보관한 처치 알림 프레임(최근 12장)을 세로로 이어 붙인 PNG 한 장으로 내려받는다. 각 프레임 위에 시각과 점수를 적는다.
  const saveFrames = useCallback(() => {
    const frames = ringRef.current.slice(-SHEET_FRAMES);
    if (!frames.length) {
      return false;
    }
    const width = frames[0].image.width;
    const height = frames[0].image.height;
    const header = 20;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = frames.length * (height + header);
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#222';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.font = '14px sans-serif';
    frames.forEach((frame, i) => {
      const y = i * (height + header);
      ctx.fillStyle = '#fff';
      const t = frame.at;
      const time = `${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}:${String(t.getSeconds()).padStart(2, '0')}`;
      ctx.fillText(`${time}  전체 ${frame.scores.full.toFixed(2)} · 위클라인 사진 ${frame.scores.portrait.toFixed(2)} · 처치 아이콘 ${frame.scores.icon.toFixed(2)} · ${frame.matched ? '위클라인 감지' : '감지 안 됨'}`, 6, y + 15);
      ctx.putImageData(frame.image, 0, y + header);
    });
    canvas.toBlob((blob) => {
      if (!blob) {
        return;
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `bser-kill-alerts-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // 다운로드가 시작된 뒤에 해제한다(바로 해제하면 브라우저에 따라 다운로드가 취소될 수 있다)
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    }, 'image/png');
    return true;
  }, []);

  return { diag, saveFrames };
}
